import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { JOTFORM_URL } from "@/constants";

const links = [
    { label: "About Us", hash: "#about", testid: "nav-link-about-us" },
    { label: "The Studio", hash: "#studio", testid: "nav-link-the-studio" },
    { label: "Capabilities", hash: "#capabilities", testid: "nav-link-capabilities" },
    { label: "Partnerships", hash: "#partnerships", testid: "nav-link-partnerships" },
    { label: "Policy", hash: "#policy", testid: "nav-link-policy" },
    { label: "Contact", hash: "#contact", testid: "nav-link-contact" },
];

const scrollTo = (hash) => (e) => {
    e.preventDefault();
    if (window.__lenis) {
        window.__lenis.scrollTo(hash, { duration: 1.8 });
    } else {
        document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
    }
};

const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        if (menuOpen) {
            window.__lenis?.stop();
        } else {
            window.__lenis?.start();
        }
    }, [menuOpen]);

    const mobileNav = (hash) => (e) => {
        e.preventDefault();
        setMenuOpen(false);
        setTimeout(() => {
            if (window.__lenis) {
                window.__lenis.scrollTo(hash, { duration: 1.6 });
            } else {
                document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
            }
        }, 350);
    };

    return (
        <>
            <motion.header
                data-testid="site-header"
                initial={{ y: -80, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 1.2, delay: 1.8, ease: [0.16, 1, 0.3, 1] }}
                className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-700 ${
                    scrolled
                        ? "border-b border-[#270057]/15 bg-white/75 backdrop-blur-xl"
                        : "border-b border-transparent bg-transparent"
                }`}
            >
                <div className="mx-auto flex max-w-[1500px] items-center justify-between gap-4 px-5 py-4 sm:px-6 sm:py-5 lg:px-16">
                    <a
                        href="#top"
                        onClick={scrollTo("#top")}
                        data-testid="nav-logo"
                        className="flex shrink-0 items-center"
                    >
                        <img
                            src="https://i.postimg.cc/D0bwrz0t/Vellor-Logo-Web.png"
                            alt="Vellor Studio"
                            className="h-10 w-auto sm:h-11"
                        />
                    </a>
                    <nav className="hidden items-center gap-6 lg:flex xl:gap-8">
                        {links.map((l) => (
                            <a
                                key={l.hash}
                                href={l.hash}
                                onClick={scrollTo(l.hash)}
                                data-testid={l.testid}
                                className="font-display text-[10px] uppercase tracking-[0.3em] text-[#270057] transition-colors duration-500 hover:text-[#270057]"
                            >
                                {l.label}
                            </a>
                        ))}
                    </nav>
                    <div className="flex items-center gap-4">
                        <a
                            href={JOTFORM_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="header-commission-project-button"
                            className="btn-royal hidden whitespace-nowrap rounded-full px-4 py-2.5 font-display text-[9px] font-semibold uppercase tracking-[0.2em] sm:inline-block sm:px-6 sm:text-[10px] sm:tracking-[0.25em]"
                        >
                            Commission a Project
                        </a>
                        <button
                            type="button"
                            data-testid="mobile-menu-button"
                            aria-label="Open menu"
                            onClick={() => setMenuOpen(true)}
                            className="flex h-11 w-11 items-center justify-center rounded-full border border-[#270057]/25 text-[#270057] transition-colors duration-300 hover:bg-[#270057] hover:text-white lg:hidden"
                        >
                            <Menu className="h-5 w-5" />
                        </button>
                    </div>
                </div>
            </motion.header>

            <AnimatePresence>
                {menuOpen && (
                    <motion.div
                        data-testid="mobile-menu"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4, ease: "easeOut" }}
                        className="fixed inset-0 z-[70] flex flex-col bg-white"
                    >
                        <div className="flex items-center justify-between border-b border-[#270057]/15 px-5 py-4">
                            <img
                                src="https://i.postimg.cc/D0bwrz0t/Vellor-Logo-Web.png"
                                alt="Vellor Studio"
                                className="h-10 w-auto"
                            />
                            <button
                                type="button"
                                data-testid="mobile-menu-close-button"
                                aria-label="Close menu"
                                onClick={() => setMenuOpen(false)}
                                className="flex h-11 w-11 items-center justify-center rounded-full border border-[#270057]/25 text-[#270057] transition-colors duration-300 hover:bg-[#270057] hover:text-white"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <nav className="flex flex-1 flex-col justify-center px-8">
                            {links.map((l, i) => (
                                <motion.a
                                    key={l.hash}
                                    href={l.hash}
                                    onClick={mobileNav(l.hash)}
                                    data-testid={`mobile-${l.testid}`}
                                    initial={{ opacity: 0, y: 24 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{
                                        duration: 0.6,
                                        delay: 0.12 + i * 0.07,
                                        ease: [0.16, 1, 0.3, 1],
                                    }}
                                    className="border-b border-[#270057]/10 py-5 font-serif text-3xl font-normal text-[#270057] transition-colors duration-300 last:border-b-0 hover:italic"
                                >
                                    {l.label}
                                </motion.a>
                            ))}
                        </nav>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.55 }}
                            className="px-8 pb-14"
                        >
                            <a
                                href={JOTFORM_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="mobile-menu-commission-button"
                                className="btn-royal block rounded-full py-5 text-center font-display text-[11px] font-semibold uppercase tracking-[0.3em]"
                            >
                                Commission a Project
                            </a>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default Navbar;
